import { Link } from "react-router-dom";
import { unifiNetworksContent } from "../content/unifi-networks.js";

function IconInstall() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 8V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconWireless() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12.5a11 11 0 0 1 14 0M8.5 15.5a7 7 0 0 1 7 0M12 18.5h.01"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

function IconSegment() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="14" y="5" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="8" y="13" width="8" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6.5 11v1.5M17.5 11v1.5M12 11v2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function IconSecurity() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 8.5V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1.5l-8 4.5-6-4.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="14" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 11v1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

const FEATURE_ICONS = {
  install: IconInstall,
  wireless: IconWireless,
  segment: IconSegment,
  security: IconSecurity,
};

export default function UnifiNetworksPage() {
  const c = unifiNetworksContent;

  return (
    <article className="site-page unifi-page">
      <div className="site-page__inner site-page__inner--wide unifi-page__inner">
        <header className="unifi-hero">
          <p className="site-page__eyebrow">{c.eyebrow}</p>
          <h1 className="site-page__title unifi-hero__title">{c.title}</h1>
          <p className="site-page__lead unifi-hero__lead">{c.lead}</p>
          <ul className="unifi-hero__points">
            {c.heroPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="site-page__cta unifi-hero__cta">
            <Link to="/contact" className="site-button-primary">
              {c.cta.primary}
            </Link>
            <Link to="/pricing" className="site-button-ghost">
              {c.cta.secondary}
            </Link>
          </p>
        </header>

        <section className="unifi-features" aria-labelledby="unifi-features-heading">
          <h2 id="unifi-features-heading" className="unifi-section-title">
            Coverage for how you actually work
          </h2>
          <div className="unifi-features__grid">
            {c.features.map((f) => {
              const Icon = FEATURE_ICONS[f.icon] ?? IconInstall;
              return (
                <article key={f.id} className="unifi-feature-card">
                  <span className="unifi-feature-card__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="unifi-capabilities" aria-labelledby="unifi-capabilities-heading">
          <h2 id="unifi-capabilities-heading" className="unifi-section-title">
            {c.capabilities.title}
          </h2>
          <ul className="unifi-capabilities__chips" aria-label="Network services">
            {c.capabilities.chips.map((chip) => (
              <li key={chip}>{chip}</li>
            ))}
          </ul>
        </section>

        <section className="unifi-stack" aria-labelledby="unifi-stack-heading">
          <h2 id="unifi-stack-heading" className="unifi-section-title">
            {c.unifiStack.title}
          </h2>
          <p className="unifi-stack__body">{c.unifiStack.body}</p>
        </section>

        <section className="unifi-support" aria-labelledby="unifi-support-heading">
          <h2 id="unifi-support-heading" className="unifi-section-title">
            {c.support.title}
          </h2>
          <p className="unifi-support__body">{c.support.body}</p>
          <ul className="unifi-support__points">
            {c.support.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>

        <section
          className="unifi-dashboard-preview"
          aria-labelledby="unifi-ticketing-heading"
        >
          <h2 id="unifi-ticketing-heading" className="unifi-section-title">
            {c.ticketing.title}
          </h2>
          <p className="unifi-dashboard-preview__subtitle">{c.ticketing.subtitle}</p>

          <div className="unifi-dash" role="img" aria-label="Sample network support ticketing dashboard">
            <div className="unifi-dash__top">
              <div>
                <p className="unifi-dash__eyebrow">Support portal</p>
                <p className="unifi-dash__business">{c.ticketing.businessName}</p>
              </div>
              <span className="unifi-dash__live">
                <span className="unifi-dash__live-dot" />
                {c.ticketing.liveBadge}
              </span>
            </div>

            <div className="unifi-dash__metrics">
              <div className="unifi-dash__metric unifi-dash__metric--highlight">
                <span className="unifi-dash__metric-value">
                  {c.ticketing.metrics.openTickets}
                </span>
                <span className="unifi-dash__metric-label">Open tickets</span>
              </div>
              <div className="unifi-dash__metric">
                <span className="unifi-dash__metric-value">
                  {c.ticketing.metrics.resolvedMonth}
                </span>
                <span className="unifi-dash__metric-label">Resolved this month</span>
              </div>
              <div className="unifi-dash__metric">
                <span className="unifi-dash__metric-value">
                  {c.ticketing.metrics.avgUpdate}
                </span>
                <span className="unifi-dash__metric-label">Typical update cadence</span>
              </div>
            </div>

            <div className="unifi-dash__panels">
              <div className="unifi-dash__panel">
                <h3>Needs attention</h3>
                <ul className="unifi-dash__active-list">
                  {c.ticketing.activeTickets.map((ticket) => (
                    <li key={ticket.id} className="unifi-dash__active-item">
                      <div className="unifi-dash__active-head">
                        <strong>{ticket.id}</strong>
                        <span className="unifi-dash__pill">{ticket.state}</span>
                      </div>
                      <p>{ticket.snippet}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="unifi-dash__panel">
                <h3>Recently closed</h3>
                <div className="unifi-dash__table-wrap">
                  <table className="unifi-dash__table">
                    <thead>
                      <tr>
                        <th scope="col">Ticket</th>
                        <th scope="col">Topic</th>
                        <th scope="col">Status</th>
                        <th scope="col">Resolution</th>
                        <th scope="col">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.ticketing.recent.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.topic}</td>
                          <td>
                            <span className="unifi-dash__outcome">{row.outcome}</span>
                          </td>
                          <td>{row.response}</td>
                          <td className="unifi-dash__time">{row.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        <p className="site-page__cta">
          <Link to="/contact" className="site-button-primary">
            {c.cta.primary}
          </Link>
          <Link to="/" className="site-button-ghost">
            ← Back to home
          </Link>
        </p>
      </div>
    </article>
  );
}
